<template>
  <GhostContent
    v-if="page"
    :data="page"
  />
</template>

<script setup lang="ts">
import { renderToString } from 'vue/server-renderer'
import { h } from 'vue'
import { GhostJobs } from '#components'
import { ROUTE_BASE_NAME } from '~/constants'
import type { JobPostings } from '~/types/ghost'

const props = defineProps<{
  pageKey: string
}>()

const { locale, t } = useI18n()
const route = useRoute()
const runtimeConfig = useRuntimeConfig()
const { getPage } = useGhostApi()
const { routeBaseName } = useBaseName()

const pageId = computed(() => t(props.pageKey))

const { data, error } = await useAsyncData(
  computed(() => `ghost-page-${props.pageKey}-${locale.value}`),
  async () => {
    const [page, jobPostings] = await Promise.all([
      getPage(pageId.value),
      routeBaseName.value === ROUTE_BASE_NAME.careers ? $fetch<JobPostings>('/jobs') : null,
    ])
    return { page, jobPostings }
  },
)

if (!data.value?.page || error.value?.status) {
  throw createError({
    statusCode: error.value?.status ?? 404,
    statusText: `There was an issue fetching the ghost page: (${props.pageKey})`,
  })
}

const { getStaticImage } = useImage()
const shareImage = computed(() => getStaticImage({
  path: '/web-app/share-img-20260601.jpg',
}))

/**
 * This renders the results form the jobs API call to a string through a
 * component. This is done because the jobs are rendered in the static HTML of
 * the page, and we need to ensure that the jobs are rendered in the correct
 * order.
 */
const jobsHtml = await renderToString(h(GhostJobs, {
  jobPostings: data.value.jobPostings,
}))

const BAMBOO_PLACEHOLDER = '<div id="bamboo-container"></div>'

if (data.value.page?.html?.includes(BAMBOO_PLACEHOLDER)) {
  data.value.page.html = data.value.page.html.replace(BAMBOO_PLACEHOLDER, jobsHtml)
}

const page = data.value.page

if (page?.feature_image) {
  useHead({
    link: [
      {
        rel: 'preload',
        as: 'image',
        href: page.feature_image,
      },
    ],
  })
}

useSeoMeta({
  title: page?.meta_title || page?.title,
  titleTemplate: null,
  description: page?.meta_description || page?.custom_excerpt,
  ogUrl: `https://www.rvezy.com${route.path}`,
  ogType: 'website',
  ogTitle: page?.og_title || page?.meta_title || page?.title,
  ogDescription: page?.og_description || page?.meta_description || page?.custom_excerpt,
  ogImage:
        page?.og_image || page?.feature_image || shareImage.value,
  fbAppId: runtimeConfig.public.facebook.appId,
  twitterCard: 'summary_large_image',
  twitterTitle: page?.twitter_title || page?.meta_title || page?.title,
  twitterDescription: page?.twitter_description || page?.meta_description || page?.custom_excerpt,
  twitterImage: page?.twitter_image || shareImage.value,
})
</script>
